import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import DataTableCard from '@/components/DataTableCard';
import api from '@/lib/api';
import { toast } from 'sonner';
import { UserPlus, Users as UsersIcon } from 'lucide-react';

const ROLES = ['admin', 'senior_partner', 'associate', 'user'];
const emptyForm = { email: '', full_name: '', password: '', role: 'associate' };

const Users = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get(`/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (user.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
        return;
      }
      fetchUsers();
    }
  }, [user, loading, navigate, fetchUsers]);

  const createUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/users`, form);
      toast.success('User created');
      setForm(emptyForm);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const changeRole = async (target, role) => {
    try {
      await api.put(
        `/users/${target.id}/role`,
        { role }
      );
      toast.success(`Role updated for ${target.email}`);
      setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, role } : u)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update role');
    }
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <PageShell>
      <PageHeader title="User Management" icon={UsersIcon} titleTestId="users-title" />

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <UserPlus size={18} /> Create User
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={createUser}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end"
            data-testid="create-user-form"
          >
            <div>
              <Label htmlFor="cu-name">Full name</Label>
              <Input
                id="cu-name"
                data-testid="cu-name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="cu-email">Email</Label>
              <Input
                id="cu-email"
                type="email"
                data-testid="cu-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="cu-password">Password</Label>
              <Input
                id="cu-password"
                type="password"
                data-testid="cu-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="cu-role">Role</Label>
              <select
                id="cu-role"
                data-testid="cu-role"
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <Button type="submit" disabled={submitting} data-testid="cu-submit">
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <DataTableCard>
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3 font-medium" scope="col">Name</th>
              <th className="p-3 font-medium" scope="col">Email</th>
              <th className="p-3 font-medium" scope="col">Role</th>
              <th className="p-3 font-medium" scope="col">Created</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t" data-testid={`user-row-${u.id}`}>
                <td className="p-3">{u.full_name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">
                  <select
                    className="border rounded-md px-2 py-1 text-sm bg-background disabled:opacity-60"
                    value={u.role}
                    disabled={u.id === user.id}
                    onChange={(e) => changeRole(u, e.target.value)}
                    data-testid={`role-select-${u.id}`}
                    aria-label={`Role for ${u.email}`}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  {u.id === user.id ? (
                    <span className="text-xs text-muted-foreground ml-2">(you)</span>
                  ) : null}
                </td>
                <td className="p-3 text-muted-foreground whitespace-nowrap">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTableCard>
    </PageShell>
  );
};

export default Users;
